import Button from "components/buttons/Button";
import { Modal } from "components/Modal/Modal";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store";
import { closeModal } from "store/modals/slice";
import styles from "./CmsFooterAppsEditModal.module.scss";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import { ModalTypes } from "configs/types";

import {
  APPLICATION_PLATFORM_KEYS,
  ApplicationSlotData,
  createApplicationItem,
  getApplicationItem,
  updateApplicationItem,
} from "store/cms/footer/applications/slice";
import { handleClearDraftItem } from "store/cms/footer/applications/slice";

import AppsMainTab from "./tabs/AppsMainTab";
import { AppsTrailTab } from "./tabs/AppsTrailTab";
import { useFormErrors } from "helpers/useFormErrors";
import { handleApiError } from "helpers/handleApiError";
import ModalSkeleton from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";

enum ModalApplicationsTabs {
  main = "main",
  trail = "trail",
}

const CmsFooterAppsEditModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const entityId = searchParams.get("entityId");
  const isEdit = Boolean(entityId);
  const formErrors = useFormErrors();

  const isItemFetched = useAppSelector(
    (store) => store["cms-applications"].isItemFetched,
  );
  const isItemFetching = useAppSelector(
    (store) => store["cms-applications"].isItemFetching,
  );
  const draftItem = useAppSelector(
    (store) => store["cms-applications"].draftItem,
  );
  const geo = useAppSelector((store) => store["settings"].geoList);

  const [activeTab, setActiveTab] = useState<ModalApplicationsTabs>(
    ModalApplicationsTabs.main,
  );

  const permissions = usePermissionHelper();

  const tabs = [ModalApplicationsTabs.main, ModalApplicationsTabs.trail];

  useEffect(() => {
    if (entityId && !isItemFetched) {
      dispatch(getApplicationItem(+entityId));
    }
  }, [entityId, isItemFetched]);

  const isLoaded = () => {
    if (entityId && !isItemFetched) return false;
    return true;
  };

  const loaded = isLoaded();

  const handleClose = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
    dispatch(handleClearDraftItem());
  };

  const canBeSaved = () => {
    if (!isLoaded()) return false;

    const hasModifiedFields = draftItem?.isModified ?? false;

    if (!draftItem?.slots) return hasModifiedFields;

    const slotsKeys = Object.keys(
      draftItem.slots,
    ) as APPLICATION_PLATFORM_KEYS[];

    const hasModifiedSlots = slotsKeys.some(
      (key) => draftItem.slots[key].isModified,
    );

    return hasModifiedFields || hasModifiedSlots;
  };

  const newUploadedImages = useRef<{
    [key in APPLICATION_PLATFORM_KEYS]?: File;
  }>({});

  const handleSave = async () => {
    formErrors.resetErrors();

    const {
      slots: slotsDraftItem,
      geo: draftGeo,
      ...restDraftItem
    } = draftItem;

    const { id, ...primaryData } = restDraftItem;

    const slotsData = (
      Object.keys(slotsDraftItem) as APPLICATION_PLATFORM_KEYS[]
    ).reduce(
      (acc, key) => {
        const slot = slotsDraftItem[key];
        const newImage = newUploadedImages.current[key];

        if (!slot.href && !newImage) return acc;

        acc[key] = {
          href: slot.href,
          ...(newImage && { image: newImage }),
        };

        return acc;
      },
      {} as Record<
        APPLICATION_PLATFORM_KEYS,
        Omit<ApplicationSlotData, "image"> & { image?: File }
      >,
    );

    const dataToUpdate = {
      ...primaryData,
      country: draftGeo || geo[0],
      slots: slotsData,
    };

    try {
      if (!entityId) {
        const result = await dispatch(
          createApplicationItem(dataToUpdate),
        ).unwrap();

        navigate(
          `${location.pathname}?activeModal=${ModalTypes.applications}&entityId=${result.id}`,
        );

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityCreateSuccess"),
            type: NotificationTypes.success,
          }),
        );

        return;
      }

      await dispatch(
        updateApplicationItem({
          id: +entityId,
          ...dataToUpdate,
        }),
      ).unwrap();

      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityUpdateSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } catch (error) {
      handleApiError({
        error,
        t,
        dispatch,
        formErrors,
      });
    }
  };

  const handleTabClick = (tab: ModalApplicationsTabs) => {
    if (!isEdit && tab !== ModalApplicationsTabs.main) {
      dispatch(
        showNotification({
          title: t("warning"),
          message: t("createEntityFirst"),
          type: NotificationTypes.warning,
        }),
      );
      return;
    }

    setActiveTab(tab);
  };

  const renderTab = () => {
    if (activeTab === ModalApplicationsTabs.main) {
      return (
        <AppsMainTab
          newUploadedImages={newUploadedImages}
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          readOnly={!permissions?.update}
        />
      );
    }

    if (activeTab === ModalApplicationsTabs.trail) {
      if (!entityId) return null;

      return <AppsTrailTab entityId={entityId} />;
    }

    return null;
  };

  return (
    <Modal onClose={handleClose}>
      <Modal.Header>{t("applications")}</Modal.Header>

      {!loaded ? (
        <ModalSkeleton />
      ) : (
        <>
          <Modal.Tabs
            tabs={tabs}
            activeTab={activeTab}
            onClick={handleTabClick}
            canBeClicked={isItemFetched}
          />

          <Modal.Content className={styles.cmsFooterPartnersContent}>
            {renderTab()}
          </Modal.Content>

          {activeTab !== ModalApplicationsTabs.trail && permissions?.update && (
            <Modal.Footer>
              <Button isSecondary txt={t("cancel")} handleClick={handleClose} />
              <Button
                isPrimary
                handleClick={handleSave}
                txt={t("save")}
                isLoad={isItemFetching}
                disabled={!canBeSaved()}
              />
            </Modal.Footer>
          )}
        </>
      )}
    </Modal>
  );
};

export default CmsFooterAppsEditModal;
